// Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { API_BASE } from "../config";
import { sanitizeAndValidateEmail, sanitizeString, validateRequired } from "../utils/inputValidation";
import { getUserFriendlyError } from "../utils/errorMessages";
import { log, logError } from "../utils/logger";

export default function Login() {
  const blue = "#3366CC";
  const text = "#0F172A";
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true"
  );

  useEffect(() => {
    if (isLoggedIn) {
      // ใช้ sessionStorage แทน localStorage เพื่อความปลอดภัย
      const redirect = sessionStorage.getItem("postLoginRedirect") || "/";
      log("[Login] redirect to:", redirect);
      sessionStorage.removeItem("postLoginRedirect");
      navigate(redirect, { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const container = {
    width: "100%",
    background: "#fff",
    overflow: "visible",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  };

  const responsiveCSS = `
  * { box-sizing: border-box; }
  
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }

  .page-container {
    width: 100% !important;
    overflow-x: hidden;
    background: #fff;
  }

  /* Login page specific responsive styles */
  .login-text-container {
    flex: 1 1 300px;
    max-width: 420px;
    color: #E5E7EB;
    text-align: left;
  }

  .login-description {
    font-size: 14px;
    color: #CBD5F5;
    max-width: 360px;
    margin: 0 0 20px;
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }

    .desktop-nav { 
      display: none !important; 
    }

    .hamburger-btn { 
      display: inline-flex !important; 
    }

    .sticky-nav {
      height: 60px !important;
      padding: 0 16px !important;
    }
    
    .grid-2 { 
      grid-template-columns: 1fr !important; 
      gap: 24px !important; 
    }

    .account-menu-panel {
      right: 10px !important;
      top: 50px !important;
      width: calc(100vw - 20px) !important;
      max-width: 300px !important;
    }

    .hero { 
      padding: 60px 20px !important; 
      min-height: auto !important;
    }

    .hero-title { 
      font-size: 32px !important; 
      text-align: center !important; 
      width: 100%;
    }

    .cta-buttons { 
      flex-direction: column !important; 
      width: 100% !important; 
      gap: 12px !important;
    }

    .cta-buttons > button { 
      width: 100% !important; 
      margin: 0 !important;
    }

    .what-is, #why-smartclick, #cta-card, #free-trial + section {
      padding: 40px 20px !important;
    }

    /* Login page mobile styles */
    .login-text-container {
      text-align: center !important;
    }

    .login-description {
      margin: 0 auto 20px !important;
    }
  }

  [id] { scroll-margin-top: 70px; }
`;

  return (
    <div style={container}>
      <style>{responsiveCSS}</style>

      <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
        <TopInfoBar blue={blue} />
        <Navbar text={text} isLoggedIn={isLoggedIn} />
      </div>

      <LoginSection setIsLoggedIn={setIsLoggedIn} />

      <Footer />
    </div>
  );
}

// ใช้ shared components แทน - ลบ functions เดิมออกแล้ว

/* ========= Login Section ========= */
function LoginSection({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navyDeep = "#0C2244";
  const blueBtn = "#0EA5E9";

  // แก้ไขตรงนี้: ใช้ width 100% และ box-sizing
  const card = {
    width: "100%",
    maxWidth: 420,
    background: navyDeep,
    borderRadius: 16,
    padding: "32px 30px 28px",
    boxShadow: "0 20px 45px rgba(15,23,42,.45)",
    color: "#fff",
    boxSizing: "border-box", // ป้องกัน padding ดันกว้างเกิน
  };

  const label = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#E6EDF7",
    marginBottom: 6,
  };

  // แก้ไขตรงนี้: เปลี่ยน width จาก 355 เป็น 100%
  const field = {
    width: "100%", 
    height: 40,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.2)",
    background: "#F8FAFC",
    padding: "0 12px",
    fontSize: 14,
    color: "#0C2244",
    outline: "none",
    boxSizing: "border-box", // สำคัญมากเพื่อให้ padding ไม่ทำให้ input กว้างเกิน 100%
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      const form = Object.fromEntries(new FormData(e.currentTarget).entries());
      
      // Sanitize และ validate input
      const username = sanitizeString((form.username || form.email || "").toString());
      const password = sanitizeString((form.password || "").toString());

      // Validate required fields
      const usernameValidation = validateRequired(username, "Username");
      const passwordValidation = validateRequired(password, "Password");
      
      if (!usernameValidation.isValid) {
        setError(usernameValidation.error);
        setLoading(false);
        return;
      }
      
      if (!passwordValidation.isValid) {
        setError(passwordValidation.error);
        setLoading(false);
        return;
      }

      // Validate email format if username looks like email
      if (username.includes("@")) {
        const emailValidation = sanitizeAndValidateEmail(username);
        if (!emailValidation.isValid) {
          setError(getUserFriendlyError(emailValidation.error, "email"));
          setLoading(false);
          return;
        }
      }

      // Log API endpoint สำหรับ debugging
      const loginUrl = `${API_BASE}/login`;
      console.log("[Login] API_BASE:", API_BASE);
      console.log("[Login] Full login URL:", loginUrl);
      log("[Login] Attempting login to:", loginUrl);
      
      const res = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      
      log("[Login] Response status:", res.status, res.statusText);

      if (!res.ok) {
        // อ่าน response body เพื่อดู error detail จาก server
        let errorDetail = "";
        try {
          const errorBody = await res.json();
          errorDetail = errorBody?.detail || errorBody?.message || "";
        } catch (e) {
          // ถ้าอ่าน JSON ไม่ได้ ให้ลองอ่านเป็น text
          try {
            errorDetail = await res.text();
          } catch (e2) {
            // ถ้าอ่านไม่ได้เลย ให้ใช้ status code
            errorDetail = `HTTP ${res.status}`;
          }
        }
        log("[Login] Error response:", res.status, errorDetail);
        throw new Error(getUserFriendlyError(res, "login"));
      }

      const body = await res.json();
      const token = body?.access_token;
      if (!token) {
        throw new Error(getUserFriendlyError("No access token received", "login"));
      }

      // ใช้ sessionStorage แทน localStorage เพื่อความปลอดภัย
      sessionStorage.setItem("isLoggedIn", "true");
      sessionStorage.setItem("access_token", token);
      sessionStorage.setItem("token_type", body?.token_type || "bearer");
      // ไม่เก็บ userEmail ใน sessionStorage เพื่อความปลอดภัย - ดึงจาก API แทน

      setIsLoggedIn(true);
      // ใช้ sessionStorage แทน localStorage เพื่อความปลอดภัย
      const raw = sessionStorage.getItem("postLoginRedirect") || "/";
      sessionStorage.removeItem("postLoginRedirect");
      // Allow only same-origin relative paths to prevent open redirect
      const redirect = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
      navigate(redirect, { replace: true });
      log("[Login] redirect to:", redirect);
    } catch (err) {
      // Log error สำหรับ debugging
      log("[Login] Error:", err);
      logError("[Login] Full error:", err.message, err.stack);
      
      // ตรวจสอบว่าเป็น network error หรือไม่
      if (err instanceof TypeError && err.message.includes("fetch")) {
        setError("Unable to connect to the server. Please check if the server is running and try again.");
      } else {
      setError(getUserFriendlyError(err, "login"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      style={{
        background: "radial-gradient(circle at top, #0f172a 0, #020617 40%, #020617 100%)",
        minHeight: "calc(100vh - 108px)", // ปรับให้พอดีหน้าจอ
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px", // ลด padding บนโมบาย
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 960,
          display: "flex",
          gap: 40,
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap", // ให้ข้อความกับ Card ตกลงมาเมื่อจอแคบ
        }}
      >
        {/* ซ้าย: ข้อความ */}
        <div className="login-text-container">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 10px",
              borderRadius: 999,
              border: "1px solid rgba(148,163,184,.5)",
              fontSize: 11,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              marginBottom: 16,
              color: "#93C5FD",
            }}
          >
            Smart Audit Portal
          </div>
          <h1 style={{ fontSize: 28, lineHeight: 1.3, fontWeight: 700, color: "#F9FAFB", margin: "0 0 10px" }}>
            Secure access to your environment
          </h1>
          <p className="login-description">
            Log in to manage licenses, track usage status, and view audit information.
          </p>
        </div>

        {/* ขวา: Card Login */}
        <form onSubmit={onSubmit} style={card}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase", color: "#7CC4FF", fontWeight: 600, marginBottom: 4 }}>
              Sign in
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              Login Smart Audit
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="username" style={label}>Username</label>
            <input id="username" type="text" name="username" placeholder="Username" required maxLength={100} style={field} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="password" style={label}>Password</label>
            <input id="password" type="password" name="password" placeholder="Password" required maxLength={128} style={field} />
          </div>

          {error && (
            <div 
              role="alert"
              aria-live="polite"
              style={{ 
                background: "#fef2f2", 
                color: "#b91c1c", 
                border: "1px solid #fecaca", 
                padding: "12px 16px", 
                borderRadius: 10, 
                fontSize: 14, 
                marginBottom: 15,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FiX size={16} /> {error}
            </div>
          )}

          <button
            disabled={loading}
            type="submit"
            aria-label={loading ? "Logging in, please wait" : "Sign in to Smart Audit"}
            style={{
              width: "100%",
              height: 44,
              background: blueBtn,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            {loading ? "Logging in..." : "Sign In"}
          </button>
        </form>
      </div>
    </section>
  );
}
// ใช้ shared Footer component แทน - ลบ function เดิมออกแล้ว
